import styled, { css } from 'styled-components'
import { zLayers } from 'utils/styling/zLayers'
import {
  EDITOR_COLOR_TILE_GRID_WIDTH_PX,
  EDITOR_FIELD_MENU_CHILD_INDENT_PX,
  EDITOR_TOOLBAR_STRIP_TOP_PX,
} from './EditorTextBoxToolbar.geometry'

export const ToolbarStrip = styled.div`
  position: absolute;
  top: ${EDITOR_TOOLBAR_STRIP_TOP_PX}px;
  z-index: ${zLayers.quickActions};
  display: flex;
  justify-content: center;
  pointer-events: none;
`

type ToolbarProps = {
  $inline?: boolean
}

export const Toolbar = styled.div<ToolbarProps>`
  ${({ $inline }) =>
    $inline
      ? css`
          position: absolute;
          bottom: 100%;
          left: 0;
          margin-bottom: 2px;
          z-index: ${zLayers.quickActions};
        `
      : css`
          position: relative;
          max-width: calc(100% - 16px);
          pointer-events: auto;
        `}
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  width: max-content;
  gap: 6px;
  white-space: nowrap;
  padding: 4px;
  background: #ffffff;
  border-radius: 4px;
  box-shadow: 0 2px 20px rgba(0, 0, 0, 0.05);
`

type BtnProps = {
  pressed?: boolean
  $disabled?: boolean
}

export const Btn = styled.button<BtnProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 4px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #0a0a0a;
  font-size: 13px;
  line-height: 1;
  cursor: pointer;
  &[hidden] {
    display: none;
  }
  &:hover {
    background: #f6fbff;
  }
  ${({ pressed }) =>
    pressed &&
    css`
      background: #c3def4;
      color: #0a0a0a;
      &:hover {
        background: #c3def4;
      }
    `}
  &[data-editor-text-mixed='true'] {
    background: #f0f6fb;
    box-shadow: inset 0 0 0 1px #9cc8e8;
    border: 1px dashed #2a8acd;
    &:hover {
      background: #e7f1fa;
    }
  }
  ${({ $disabled }) =>
    $disabled &&
    css`
      opacity: 0.4;
      cursor: not-allowed;
      &:hover {
        background: transparent;
      }
    `}
`

type FieldProps = {
  $active?: boolean
  /** Sized to its label: the insert and role fields, whose text never changes. */
  $fit?: boolean
}

export const Field = styled.button<FieldProps>`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  width: ${({ $fit }) => ($fit ? 'auto' : '120px')};
  height: 32px;
  padding: 8px;
  border: 1px solid ${({ $active }) => ($active ? '#2a8acd' : '#e5e5e5')};
  border-radius: 4px;
  background: #ffffff;
  color: #0a0a0a;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
  ${({ $active }) =>
    $active &&
    css`
      box-shadow: 0 0 0 1px #2a8acd;
    `}
`

export const FieldLabel = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
`

type CaretProps = {
  /** Turned to point up: the popover or group the caret belongs to is open. */
  $up?: boolean
}

/**
 * A field's or group row's caret: the down chevron, turned while its popover
 * or group is open. One asset in both states, turned by CSS — an icon whose
 * source changes goes through react-inlinesvg's fetch again and paints
 * nothing for a frame, and a fit-to-label field without its caret is
 * narrower, which moves the centred toolbar.
 */
export const Caret = styled.span<CaretProps>`
  display: inline-flex;
  flex: 0 0 auto;
  transform: rotate(${({ $up }) => ($up ? 180 : 0)}deg);
`

export const FieldGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 32px;
  padding: 4px;
  border: 1px solid #e5e5e5;
  border-radius: 4px;
  background: #ffffff;
`

/**
 * The marker on the size field of a Fill text box whose maximum is what
 * holds the size at the caret. The field shows the painted size and the row
 * keeps the size the person typed — the one place the two differ by design —
 * so the field says so rather than leaving it to be read as a defect.
 */
export const AtMaxBadge = styled.span`
  display: inline-flex;
  align-items: center;
  height: 18px;
  padding: 0 5px;
  border-radius: 3px;
  background: #f1f1f1;
  color: #6b6b6b;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.02em;
  line-height: 1;
  white-space: nowrap;
  cursor: default;
  user-select: none;
`

export const SizeInput = styled.input`
  width: 44px;
  height: 24px;
  padding: 0 2px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #0a0a0a;
  font-size: 14px;
  font-family: inherit;
  line-height: 1;
  text-align: center;
  cursor: text;
  outline: none;
  &:hover {
    background: #f5f5f5;
  }
  &:focus {
    background: #f5f5f5;
  }
`

export const Sep = styled.span`
  width: 1px;
  height: 28px;
  flex: 0 0 1px;
  background: rgba(0, 0, 0, 0.07);
`

type SwatchProps = {
  hex?: string
}

export const Swatch = styled.span<SwatchProps>`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 2px;
  background: ${({ hex }) => hex || '#000000'};
`

/** Five 30px tiles a row, no gap: TinyMCE's swatch grid. */
export const TileGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: ${EDITOR_COLOR_TILE_GRID_WIDTH_PX}px;
  &[hidden] {
    display: none;
  }
`

type TileProps = {
  /** The remove and custom tiles: a glyph on white with the hover tint. */
  $glyph?: boolean
}

export const Tile = styled.button<TileProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 0;
  border: none;
  background: #ffffff;
  cursor: pointer;
  ${({ $glyph }) =>
    $glyph
      ? css`
          &:hover {
            background: #dee0e2;
          }
        `
      : css`
          &:hover {
            box-shadow: inset 0 0 0 1px rgba(127, 127, 127, 0.3);
            transform: scale(0.8);
          }
        `}
`

export const HexView = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
  &[hidden] {
    display: none;
  }
`

export const TextBtn = styled.button`
  align-self: flex-start;
  padding: 2px 4px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #0a0a0a;
  font-size: 12px;
  cursor: pointer;
  &:hover {
    background: #f6fbff;
  }
`

type PopoverProps = {
  /**
   * A list popover: rows flush to the edges, hairline border, soft shadow.
   * `'scrolling'` dresses a popover of controls the same way (the shadow
   * menu's steppers over its colour view); holding no list of its own to
   * scroll, the popover itself scrolls where the window caps its height.
   */
  $menu?: boolean | 'scrolling'
  /** A row of icon tiles: the toolbar's own padding and gap, 32px tall. */
  $pill?: boolean
  /** Placed by its parent rather than hung from the toolbar. */
  $static?: boolean
  /**
   * Portaled to the body and fixed at the viewport position its owner sets
   * (`--editor-popover-left/top`): nothing between it and the window clips
   * it — not the canvas, which scrolls and would cut a popover that reaches
   * past its edge, not a modal's dialog, which scrolls too, and not the side
   * panels, which it may overlap.
   */
  $floating?: boolean
  /**
   * What the floating popover paints above: the canvas's panels and item
   * chrome (`canvas`, the toolbar's popovers, which a modal's backdrop must
   * still cover), or a modal's backdrop as well (`modal`, the style bar's
   * popovers inside a dialog).
   */
  $layer?: 'canvas' | 'modal'
}

/**
 * The floating chrome's stacking. A canvas popover sits with the item
 * control overlay, under any modal backdrop (`itemControlOverlay + 1`); a
 * modal's popover sits above that backdrop; a flyout opens from either and
 * takes the value above both.
 */
export const EDITOR_FLOATING_POPOVER_Z = {
  canvas: zLayers.itemControlOverlay,
  modal: zLayers.itemControlOverlay + 2,
} as const
export const EDITOR_FLOATING_FLYOUT_Z = zLayers.itemControlOverlay + 3

/** The toolbar's thin scrollbar: a 3px grey thumb over no track. */
const thinScrollbar = css`
  &::-webkit-scrollbar {
    width: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: #d4d4d4;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
`

export const Popover = styled.div<PopoverProps>`
  position: absolute;
  top: 100%;
  left: var(--editor-menu-left, 0px);
  margin-top: 4px;
  z-index: 20;
  ${({ $floating, $layer }) =>
    $floating &&
    css`
      position: fixed;
      top: var(--editor-popover-top, 0px);
      left: var(--editor-popover-left, 0px);
      margin-top: 0;
      max-height: var(--editor-popover-max-h, none);
      overflow-y: auto;
      z-index: ${EDITOR_FLOATING_POPOVER_Z[$layer ?? 'canvas']};
    `}
  padding: 8px;
  background: #ffffff;
  border-radius: 3px;
  box-shadow: 0 2px 20px rgba(0, 0, 0, 0.1);
  ${({ $menu }) =>
    $menu &&
    css`
      padding: 0;
      border: 1px solid #e5e5e5;
      border-radius: 4px;
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
      overflow-x: hidden;
      overflow-y: ${$menu === 'scrolling' ? 'auto' : 'hidden'};
    `}
  /* WebKit takes a scrollbar's room from the content of a popover sized to
     its content instead of widening the popover: the thin bar fits inside
     the hex view's 8px padding, where the platform's 15px bar would cut the
     picker off. */
  ${({ $menu }) => $menu === 'scrolling' && thinScrollbar}
  ${({ $pill }) =>
    $pill &&
    css`
      padding: 4px;
    `}
  ${({ $static }) =>
    $static &&
    css`
      position: relative;
      top: auto;
      left: auto;
      margin-top: 0;
    `}
`

export const Pill = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  height: 24px;
`

/** Rows of pills in one popover, one under the other. */
export const PillRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

type MenuListProps = {
  $tall?: boolean
  $width?: string
  /** Capped at the canvas bottom (`--editor-menu-max-h`, set by the toolbar). */
  $canvasBound?: boolean
}

export const MenuList = styled.div<MenuListProps>`
  max-height: ${({ $tall, $canvasBound }) =>
    $canvasBound
      ? 'var(--editor-menu-max-h, 240px)'
      : $tall
        ? 'min(80vh, 880px)'
        : '240px'};
  min-width: 180px;
  /* A list whose rows change width while open (expanding groups) needs a
     fixed width: the popover is edge-anchored, so a width change walks its
     other edge out from under the pointer. */
  width: ${({ $width }) => $width ?? 'auto'};
  overflow-y: auto;
  ${thinScrollbar}
`

/** The one width of the toolbar's list popovers: font, role and field. */
export const MENU_LIST_WIDTH = '210px'

/** The inset of a list row's label from the list's edge. */
const MENU_ROW_INSET_PX = 16

export const MenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-width: 0;
  min-height: 44px;
  padding: 14px ${MENU_ROW_INSET_PX}px;
  border: none;
  background: transparent;
  color: #0a0a0a;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  text-align: left;
  white-space: normal;
  cursor: pointer;
  &:hover {
    background: #f6fbff;
  }
`

/** The tick at the end of a list's current row: the pick the selection resolves to. */
export const MenuCheck = styled.span`
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  margin-left: auto;
`

/**
 * A row that opens more rows — a flyout beside the list, or the rows under
 * it: a menu item with its chevron at the far edge, tinted while open as
 * while hovered.
 */
export const MenuGroupRow = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  min-height: 44px;
  padding: 14px ${MENU_ROW_INSET_PX}px;
  border: none;
  background: transparent;
  color: #0a0a0a;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  text-align: left;
  cursor: pointer;
  &:hover,
  &[aria-expanded='true'] {
    background: #f6fbff;
  }
`

/** The hairline between a list's rows and the row that leaves it (the admin authoring row). */
export const MenuRule = styled.div`
  height: 1px;
  background: #e5e5e5;
`

/** A group row's chevron: right for a flyout, down and up for rows beneath. */
export const MenuGroupChevron = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  color: #a3a3a3;
`

/**
 * The rows under an expanded field group. Each row's insert item starts its
 * label `EDITOR_FIELD_MENU_CHILD_INDENT_PX` further in than the group row
 * does, so a child reads as the group's and not as a sibling of it; the
 * details button ending the row stays flush at the end. No display of its
 * own, so the `hidden` attribute of a collapsed group still hides it.
 */
export const FieldMenuRows = styled.div`
  ${MenuItem} {
    padding-left: ${MENU_ROW_INSET_PX + EDITOR_FIELD_MENU_CHILD_INDENT_PX}px;
  }
`

/** A field row: the insert item, then its details button flush at the end. */
export const FieldMenuSplit = styled.div`
  display: flex;
  align-items: stretch;
`

/** The details button ending a field row: the group chevron's glyph in a row-tall hover box. */
export const FieldMenuConfigure = styled.button`
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 40px;
  padding: 0;
  border: none;
  background: transparent;
  color: #a3a3a3;
  cursor: pointer;
  &:hover {
    background: #f6fbff;
    color: #0a0a0a;
  }
`

type FlyoutProps = {
  /** A tile-grid flyout: flush tiles inside the menu border, no padding. */
  $grid?: boolean
}

export const Flyout = styled.div<FlyoutProps>`
  position: fixed;
  z-index: ${EDITOR_FLOATING_FLYOUT_Z};
  max-height: min(70vh, 560px);
  overflow-y: auto;
  padding: 6px;
  background: #ffffff;
  border-radius: 3px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.18);
  ${({ $grid }) =>
    $grid &&
    css`
      padding: 0;
      border: 1px solid #e5e5e5;
      border-radius: 4px;
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
      overflow: hidden;
    `}
`

/** The outline popover's body: the width stepper over the colour row. */
export const OutlineMenu = styled.div`
  width: 150px;
`

type StepperRowProps = {
  /** Ruled above instead of below: a stepper under a tile grid. */
  $top?: boolean
}

export const StepperRow = styled.div<StepperRowProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  padding: 8px 12px;
  ${({ $top }) =>
    $top
      ? css`
          border-top: 1px solid #e5e5e5;
        `
      : css`
          border-bottom: 1px solid #e5e5e5;
        `}
`

export const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
`

export const TextInput = styled.input`
  width: ${({ width }) => width || '90px'};
  height: 28px;
  padding: 0 8px;
  border: 1px solid #e5e5e5;
  border-radius: 4px;
  font-size: 13px;
  color: #0a0a0a;
  &:focus {
    outline: none;
    border-color: #2a8acd;
    box-shadow: 0 0 1px 2px #61ade4;
  }
`

export const ApplyBtn = styled.button`
  height: 28px;
  padding: 0 12px;
  border: none;
  border-radius: 4px;
  background: #2a8acd;
  color: #ffffff;
  font-size: 12px;
  cursor: pointer;
  &:hover {
    background: #1b6cab;
  }
`
